import { useEffect, useRef, useState } from 'react';
import { MdAdd, MdClose, MdContentCopy, MdExpandMore, MdPerson } from 'react-icons/md';
import izenuAPI from '../../services/izenuAPI';

// Performers and contributors of a track — the same as the web app's studio.
// The uploaded artist is the main artist; new uploads need a composer and a
// producer (the server enforces it too).

export const PERFORMER_ROLES = [['primary', 'Artista principal'], ['featuring', 'Participação']];
export const CONTRIBUTOR_ROLES = [['composer', 'Compositor'], ['producer', 'Produtor']];
const ROLE_LABEL = Object.fromEntries([...PERFORMER_ROLES, ...CONTRIBUTOR_ROLES,
    ['remixer', 'Remix'], ['lyricist', 'Letrista'], ['arranger', 'Arranjador'], ['mixing', 'Mistura'], ['mastering', 'Masterização']]);
const isPerformer = (c) => c.role === 'primary' || c.role === 'featuring' || c.role === 'remixer';

/** What a new upload still lacks, e.g. ['compositor', 'produtor']. */
export function missingCore(credits) {
    const roles = new Set((credits || []).map((c) => c.role));
    const out = [];
    if (!roles.has('composer')) out.push('compositor');
    if (!roles.has('producer')) out.push('produtor');
    return out;
}

const CDN = 'https://dpagz47n4g1lw.cloudfront.net';
const avatar = (h) => (!h.photo ? '' : h.photo.startsWith('http') ? h.photo : `${CDN}/users/${h.user_id}/profile_photos/${h.photo}`);
const inputCls = 'block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-amber-500 focus:ring-amber-500';

function Chip({ label, role, linked, onRemove, disabled }) {
    return (
        <span className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-gray-100 py-1 pl-3 pr-1.5 text-xs">
            {linked && <MdPerson size={12} className="shrink-0 text-amber-600" aria-label="Perfil na Izenu" />}
            <span className="truncate font-medium text-gray-900">{label}</span>
            <span className="whitespace-nowrap text-gray-500">· {role}</span>
            {onRemove ? (
                <button type="button" onClick={onRemove} disabled={disabled} aria-label={`Remover ${label}`}
                    className="ml-0.5 flex h-5 w-5 items-center justify-center rounded-full text-gray-400 hover:bg-red-50 hover:text-red-600">
                    <MdClose size={13} />
                </button>
            ) : <span className="w-1" />}
        </span>
    );
}

function AddRow({ roles, defaultRole, search, placeholder, onAdd, disabled }) {
    const [name, setName] = useState('');
    const [userId, setUserId] = useState(null);
    const [role, setRole] = useState(defaultRole || roles[0][0]);
    const [hits, setHits] = useState([]);
    const [open, setOpen] = useState(false);
    const timer = useRef(null);

    useEffect(() => () => clearTimeout(timer.current), []);

    const type = (v) => {
        setName(v);
        setUserId(null);
        if (!search) return;
        clearTimeout(timer.current);
        if (v.trim().length < 2) { setHits([]); setOpen(false); return; }
        timer.current = setTimeout(() => {
            izenuAPI.get('/artist/lookup.php', { params: { q: v.trim() } })
                .then(({ data }) => { const h = Array.isArray(data?.data) ? data.data : []; setHits(h); setOpen(h.length > 0); })
                .catch(() => setHits([]));
        }, 250);
    };

    const submit = () => {
        if (!name.trim()) return;
        onAdd({ name, role, user_id: userId });
        setName(''); setUserId(null); setHits([]); setOpen(false);
    };

    return (
        <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-[1fr_170px_auto]">
            <div className="relative">
                {userId && <MdPerson size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-amber-600" />}
                <input className={`${inputCls} ${userId ? '!pl-8' : ''}`} value={name} maxLength={150} disabled={disabled}
                    placeholder={placeholder} aria-label="Nome"
                    onChange={(e) => type(e.target.value)}
                    onFocus={() => setOpen(hits.length > 0)}
                    onBlur={() => setTimeout(() => setOpen(false), 150)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }} />
                {open && (
                    <ul className="absolute left-0 right-0 z-20 mt-1 overflow-hidden rounded-lg !border !border-solid border-gray-200 bg-white shadow-lg [&_li]:!p-0">
                        {hits.map((h) => (
                            <li key={h.user_id}>
                                <button type="button" onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => { setName(h.name); setUserId(h.user_id); setOpen(false); }}
                                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-900 hover:bg-amber-50">
                                    {avatar(h) ? <img src={avatar(h)} alt="" className="h-6 w-6 rounded-full bg-gray-100 object-cover" />
                                        : <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100"><MdPerson size={14} className="text-gray-400" /></span>}
                                    <span className="truncate">{h.name}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            <select className={inputCls} value={role} disabled={disabled} aria-label="Função" onChange={(e) => setRole(e.target.value)}>
                {roles.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <button type="button" onClick={submit} disabled={disabled || !name.trim()}
                className="flex items-center justify-center gap-1 rounded-lg !border !border-solid border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40">
                <MdAdd size={16} /> Adicionar
            </button>
        </div>
    );
}

/** "1 participação, 2 compositores, 1 produtor" */
function summary(credits) {
    const count = (role) => credits.filter((c) => c.role === role).length;
    return [[count('primary'), 'artista principal', 'artistas principais'], [count('featuring'), 'participação', 'participações'],
        [count('composer'), 'compositor', 'compositores'], [count('producer'), 'produtor', 'produtores']]
        .filter(([n]) => n > 0).map(([n, one, many]) => `${n} ${n === 1 ? one : many}`).join(', ');
}

export default function CreditsInputs({ value, onChange, mainArtist, required, showErrors, onCopyPrevious, disabled, defaultOpen }) {
    const [isOpen, setOpen] = useState(!!defaultOpen);
    const list = value || [];
    const performers = list.filter(isPerformer);
    const contributors = list.filter((c) => !isPerformer(c));
    const missing = required ? missingCore(list) : [];

    const add = (c) => {
        const name = c.name.trim().replace(/\s+/g, ' ');
        if (!name || list.some((v) => v.role === c.role && v.name.toLowerCase() === name.toLowerCase())) return;
        onChange([...list, { ...c, name }]);
    };
    const remove = (c) => onChange(list.filter((v) => v !== c));
    // A failed send opens it, so what is missing is right there.
    const open = isOpen || (showErrors && missing.length > 0);
    const brief = summary(list);

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
                {/* Same look as the "Mais detalhes" toggle below it. */}
                <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className="flex min-w-0 items-center gap-1 text-left text-xs text-amber-700 hover:underline">
                    <MdExpandMore size={14} className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
                    <span>{open ? 'Menos créditos' : 'Créditos: intérpretes e contribuidores'}{required && <span className="text-red-500"> *</span>}</span>
                    {!open && (
                        <span className="truncate text-xs">
                            {brief && <span className="text-gray-500"> · {brief}</span>}
                            {missing.length > 0 && <span className={showErrors ? 'text-red-600' : 'text-amber-700'}> · falta {missing.join(' e ')}</span>}
                        </span>
                    )}
                </button>
                {onCopyPrevious && (
                    <button type="button" onClick={onCopyPrevious} disabled={disabled} className="flex items-center gap-1 text-xs text-amber-700 hover:underline">
                        <MdContentCopy size={13} /> Copiar créditos da faixa anterior
                    </button>
                )}
            </div>

            {open && (<>
            <div className="space-y-2">
                <p className="text-xs text-gray-500">Intérpretes</p>
                <div className="flex flex-wrap gap-2">
                    <Chip label={mainArtist || 'Artista'} role="Artista principal" />
                    {performers.map((c, i) => <Chip key={`${c.name}-${c.role}-${i}`} label={c.name} role={ROLE_LABEL[c.role]} linked={!!c.user_id} disabled={disabled} onRemove={() => remove(c)} />)}
                </div>
                <AddRow roles={PERFORMER_ROLES} defaultRole="featuring" search disabled={disabled}
                    placeholder="Procurar artista na Izenu ou escrever o nome" onAdd={add} />
            </div>

            <div className="space-y-2">
                <p className="text-xs text-gray-500">Contribuidores{required && ' · obrigatório pelo menos um compositor e um produtor'}</p>
                {contributors.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                        {contributors.map((c, i) => <Chip key={`${c.name}-${c.role}-${i}`} label={c.name} role={ROLE_LABEL[c.role]} disabled={disabled} onRemove={() => remove(c)} />)}
                    </div>
                )}
                <AddRow roles={CONTRIBUTOR_ROLES} placeholder="Nome completo" disabled={disabled} onAdd={add} />
                {missing.length > 0 && (
                    <p className={`text-xs ${showErrors ? 'text-red-600' : 'text-amber-700'}`}>Falta: {missing.join(' e ')}.</p>
                )}
            </div>
            </>)}
        </div>
    );
}
